import useSWR from 'swr';
import { fetcher } from '../lib/fetcher';
import { useAppBridge } from '@shopify/app-bridge-react';

export type OnboardingStepStatus = 'complete' | 'incomplete' | 'unknown';

export interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  status: OnboardingStepStatus;
  /** `external` links leave the embedded app and must open in a new top-level tab. */
  action?: { label: string; href: string; external: boolean };
  warning?: string;
}

export interface OnboardingGuide {
  steps: OnboardingStep[];
  completedCount: number;
  totalCount: number;
  complete: boolean;
  /** True when the shop was set up on the legacy v1 injected templates. */
  legacy: boolean;
  /** v1→v2 migration nudge; present only when prompting an established v1 shop. */
  migration: OnboardingStep | null;
}

/**
 * Live setup-guide state.
 *
 * Called from both the dashboard (which hides itself until onboarding finishes) and the guide
 * card itself; SWR dedupes them onto one request. `revalidateOnFocus` is switched back on for
 * this key alone, because the theme-editor steps open in a new tab, so returning to the app is
 * exactly the moment the answer is most likely to have changed.
 */
export default function useOnboarding() {
  const app = useAppBridge();
  const { data, error, isValidating, mutate } = useSWR<OnboardingGuide>(
    `${process.env.APP_URL}/api/onboarding`,
    (input) => fetcher(app, input),
    { revalidateOnFocus: true },
  );

  return {
    guide: data,
    // Treat an outright failure as "nothing to gate on" rather than trapping the merchant on an
    // empty page: a broken onboarding endpoint must not cost them their dashboard.
    loading: !data && !error,
    failed: !!error,
    isValidating,
    mutate,
  };
}
